import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getDashboardSummary } from "../../services/dashboardService";
import { useApp } from "../../context/AppContext";

const styles = {
  page: {
    padding: "28px 36px",
    background: "#fbf7fa",
    minHeight: "100%",
  },
  heroTitle: {
    fontSize: "24px",
    fontWeight: 800,
    color: "#8f1632",
    margin: 0,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
  },
  heroSubtitle: {
    color: "#ff4f9a",
    marginTop: 8,
    marginBottom: 28,
    fontWeight: 600,
  },
  sectionTitle: {
    fontSize: "24px",
    fontWeight: 800,
    margin: "0 0 8px 0",
    color: "#111827",
  },
  sectionDesc: {
    margin: "0 0 22px 0",
    color: "#374151",
    lineHeight: 1.6,
  },
  grid4: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 16,
    marginBottom: 24,
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 20,
  },
  card: {
    background: "#ffffff",
    border: "1px solid #f1d5df",
    borderRadius: 18,
    padding: 20,
    boxShadow: "0 8px 20px rgba(143, 22, 50, 0.04)",
  },
  statLabel: {
    color: "#6b7280",
    fontSize: 14,
    marginBottom: 8,
  },
  statValue: {
    fontSize: 40,
    fontWeight: 800,
    color: "#111827",
    lineHeight: 1.1,
    marginBottom: 10,
  },
  statHint: {
    color: "#9ca3af",
    fontSize: 13,
  },
  actionRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 14,
  },
  actionBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid #f3c7d7",
    background: "#fff",
    color: "#8f1632",
    fontWeight: 700,
    textDecoration: "none",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    marginTop: 14,
  },
  listItem: {
    padding: 14,
    border: "1px solid #f3e4ea",
    borderRadius: 14,
    background: "#fffdfd",
  },
  itemTitle: {
    margin: 0,
    fontWeight: 700,
    color: "#111827",
  },
  itemMeta: {
    marginTop: 6,
    color: "#6b7280",
    fontSize: 13,
    lineHeight: 1.5,
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: "#fde7ef",
    color: "#8f1632",
    fontWeight: 700,
    fontSize: 12,
  },
  siteBox: {
    marginTop: 20,
    padding: 16,
    borderRadius: 16,
    background: "#fff",
    border: "1px solid #f1d5df",
  },
  empty: {
    padding: "16px 18px",
    borderRadius: 14,
    background: "#fff",
    border: "1px dashed #f1c2d3",
    color: "#6b7280",
  },
  loading: {
    padding: 16,
    borderRadius: 14,
    background: "#fff",
    border: "1px solid #f1d5df",
    color: "#6b7280",
  },
};

function asArray(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.data)) return value.data;
  return [];
}

function getCount(value) {
  if (typeof value === "number") return value;
  if (typeof value?.count === "number") return value.count;
  if (Array.isArray(value)) return value.length;
  if (Array.isArray(value?.results)) return value.results.length;
  if (Array.isArray(value?.items)) return value.items.length;
  if (Array.isArray(value?.data)) return value.data.length;
  return 0;
}

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function DashboardHome() {
  const { site, siteSettingsMap } = useApp();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        const data = await getDashboardSummary();
        if (!active) return;
        setSummary(data || {});
      } catch (error) {
        if (!active) return;
        setSummary({});
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, []);

  const totalProducts = useMemo(
    () => getCount(summary?.totalProducts ?? summary?.products),
    [summary]
  );
  const totalOrders = useMemo(
    () => getCount(summary?.totalOrders ?? summary?.orders),
    [summary]
  );
  const totalBlogs = useMemo(
    () => getCount(summary?.totalBlogs ?? summary?.blogs),
    [summary]
  );
  const totalMessages = useMemo(
    () => getCount(summary?.totalMessages ?? summary?.messages),
    [summary]
  );

  const recentOrders = asArray(summary?.orders).slice(0, 4);
  const recentMessages = asArray(summary?.messages).slice(0, 4);

  const siteName =
    site?.name ||
    siteSettingsMap?.site_name ||
    "Solusi IoT Cerdas";

  const siteSlug =
    site?.slug ||
    siteSettingsMap?.site_slug ||
    "solusi-iot-cerdas";

  return (
    <div style={styles.page}>
      <h1 style={styles.heroTitle}>Dashboard Solusi IoT Cerdas</h1>
      <div style={styles.heroSubtitle}>
        Kelola data dan pengaturan untuk website Solusi IoT Cerdas.
      </div>

      <h2 style={styles.sectionTitle}>Ringkasan Dashboard</h2>
      <p style={styles.sectionDesc}>
        Pantau performa konten, produk, order, dan aktivitas website dari satu
        tempat.
      </p>

      {loading ? <div style={styles.loading}>Memuat ringkasan dashboard...</div> : null}

      {!loading ? (
        <>
          <div style={styles.grid4}>
            <div style={styles.card}>
              <div style={styles.statLabel}>🌱 Total Produk</div>
              <div style={styles.statValue}>{totalProducts}</div>
              <div style={styles.statHint}>Data dari backend catalog</div>
            </div>

            <div style={styles.card}>
              <div style={styles.statLabel}>🛒 Total Order</div>
              <div style={styles.statValue}>{totalOrders}</div>
              <div style={styles.statHint}>Data dari backend orders</div>
            </div>

            <div style={styles.card}>
              <div style={styles.statLabel}>📰 Artikel Blog</div>
              <div style={styles.statValue}>{totalBlogs}</div>
              <div style={styles.statHint}>Draft dan published dari CMS</div>
            </div>

            <div style={styles.card}>
              <div style={styles.statLabel}>✉️ Pesan Masuk</div>
              <div style={styles.statValue}>{totalMessages}</div>
              <div style={styles.statHint}>Pesan dari form kontak / lead</div>
            </div>
          </div>

          <div style={styles.grid2}>
            <div style={styles.card}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Order Terbaru</h3>
                  <div style={styles.itemMeta}>Ringkasan pesanan yang baru masuk</div>
                </div>
                <span style={styles.badge}>{totalOrders} total</span>
              </div>

              <div style={styles.list}>
                {recentOrders.length ? (
                  recentOrders.map((order, index) => (
                    <div key={order?.id || index} style={styles.listItem}>
                      <p style={styles.itemTitle}>
                        {order?.customer_name || order?.name || order?.order_number || `Order #${order?.id || index + 1}`}
                      </p>
                      <div style={styles.itemMeta}>
                        Status: {order?.status || "menunggu"} <br />
                        Tanggal: {formatDate(order?.created_at || order?.updated_at)}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={styles.empty}>Belum ada order terbaru.</div>
                )}
              </div>

              <div style={styles.actionRow}>
                <Link to="/dashboard/orders" style={styles.actionBtn}>
                  Lihat Semua Order
                </Link>
              </div>
            </div>

            <div style={styles.card}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Pesan Terbaru</h3>
                  <div style={styles.itemMeta}>Aktivitas pesan dari website</div>
                </div>
                <span style={styles.badge}>{totalMessages} total</span>
              </div>

              <div style={styles.list}>
                {recentMessages.length ? (
                  recentMessages.map((msg, index) => (
                    <div key={msg?.id || index} style={styles.listItem}>
                      <p style={styles.itemTitle}>
                        {msg?.name || msg?.sender_name || msg?.subject || "Pesan Masuk"}
                      </p>
                      <div style={styles.itemMeta}>
                        {msg?.email || "-"} <br />
                        {formatDate(msg?.created_at || msg?.updated_at)}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={styles.empty}>Belum ada pesan terbaru.</div>
                )}
              </div>

              <div style={styles.actionRow}>
                <Link to="/dashboard/messages" style={styles.actionBtn}>
                  Buka Pesan Masuk
                </Link>
              </div>
            </div>
          </div>

          <div style={styles.siteBox}>
            <div style={{ fontWeight: 800, marginBottom: 8 }}>Site Aktif</div>
            <div style={{ color: "#111827", fontWeight: 700 }}>{siteName}</div>
            <div style={styles.itemMeta}>Slug: {siteSlug}</div>
          </div>
        </>
      ) : null}
    </div>
  );
}